import { Badge, Button, DropdownMenu } from '@cloudflare/kumo';
import { ICONS } from '../ui/icons';
import type { Deal, Stage } from '../../services/funnelApi';

type Props = {
  deal: Deal;
  selected: boolean;
  onSelect: (dealId: string) => void;
  /** Estágios do pipeline — alimentam o menu "Mover para…" (alternativa ao arrastar) */
  stages: Stage[];
  onMove: (dealId: string, toStageId: string) => void;
  /** id do texto que explica como mover sem arrastar */
  hintId?: string;
};

/**
 * Card individual. Arrastar (HTML5 DnD) coloca o id do deal no dataTransfer;
 * a coluna alvo lê no `onDrop`. Clique no card abre o detalhe.
 *
 * Teclado: o título é um botão (Enter/Espaço abre o detalhe) e o botão "…"
 * abre o menu "Mover para <estágio>", que faz o mesmo que arrastar.
 */
export function DealCard({ deal, selected, onSelect, stages, onMove, hintId }: Props) {
  const amount = Number(deal.amount) || 0;
  const formatted = amount.toLocaleString('pt-BR', { style: 'currency', currency: deal.currency || 'BRL', maximumFractionDigits: 0 });
  const targets = stages.filter((s) => s.id !== deal.stageId);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('text/deal-id', deal.id);
    e.dataTransfer.effectAllowed = 'move';
    (e.currentTarget as HTMLDivElement).classList.add('is-dragging');
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLDivElement).classList.remove('is-dragging');
  };

  return (
    // O clique em qualquer ponto do card abre o detalhe (atalho de mouse); o
    // teclado usa o botão do título, cujo clique sobe até aqui.
    <li className="funil-card-item" onClick={() => onSelect(deal.id)}>
      <div
        className={`funil-card${selected ? ' selected' : ''}`}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        data-testid={`funil-card-${deal.id}`}
        data-deal-id={deal.id}
      >
        <div className="funil-card-head">
          <button
            type="button"
            className="funil-card-open"
            aria-describedby={hintId}
            aria-current={selected ? 'true' : undefined}
          >
            <span className="font-semibold">{deal.title}</span>
          </button>
          {targets.length > 0 && (
            // Cliques no menu (renderizado em portal) sobem pela árvore do React:
            // barra aqui para não abrir o detalhe junto.
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenu.Trigger
                  render={<Button variant="ghost" shape="square" icon={ICONS.Ellipsis} aria-label={`Mover “${deal.title}” para outro estágio`} />}
                />
                <DropdownMenu.Content>
                  {targets.map((s) => (
                    <DropdownMenu.Item key={s.id} icon={ICONS.ArrowRight} onClick={() => onMove(deal.id, s.id)}>
                      Mover para {s.name}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu>
            </div>
          )}
        </div>
        <div className="funil-card-meta">
          <span className="font-semibold funil-card-amount">{formatted}</span>
          {deal.contact?.nome ? <Badge variant="neutral">{deal.contact.nome}</Badge> : null}
        </div>
        <span className="text-xs text-kumo-subtle funil-card-meta">
          <span>{deal.owner?.nome ?? 'Sem responsável'}</span>
          {deal.expectedCloseAt ? (
            <time dateTime={deal.expectedCloseAt}>
              {new Date(deal.expectedCloseAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
            </time>
          ) : null}
        </span>
      </div>
    </li>
  );
}
